import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { Group } from "@domain/group/group";
import { Profile } from "@domain/user/profile";
import { useIsMobile } from "@utils/hooks";
import { mobile } from "@ui/styles/utils";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { PrimaryBold } from "@ui/components/containers/primary";
import { row, Row, stack } from "@ui/components/layout";
import { UserDetailedView } from "@ui/components/profile/userDetailedView";
import { useAdminLimitModal } from "@ui/components/group/shareroom/useAdminLimitModal";
import { useMembersEditor } from "@ui/components/group/useMembersEditor";

interface AdministratorListProps extends React.HTMLAttributes<HTMLDivElement> {
	members: Profile[];
	shareroom: Group;
	profile: CurrentProfile | null;
	limitReached?: boolean;
}

export const AdministratorList: React.FC<AdministratorListProps> = ({
	members,
	shareroom,
	limitReached,
	profile,
	...htmlProps
}) => {
	const { owner } = shareroom;

	const isOwner = owner.id === profile?.id;

	const openMembersModal = useMembersEditor(shareroom, "ADMINISTRATOR");
	const openLimitModal = useAdminLimitModal();
	const isMobile = useIsMobile();

	return (
		<Container {...htmlProps}>
			<Row align="center" justify="space-between" style={{ width: "100%" }}>
				<StyledPrimarybold>{I18n.t("shareroom.about.teachers.title")}</StyledPrimarybold>
				{isOwner ? (
					<RoundedPlusButton
						onClick={() =>
							limitReached
								? openLimitModal()
								: openMembersModal(I18n.t("group.DISCUSSION_GROUP.public.invitation_button"))
						}
					/>
				) : null}
			</Row>
			<Bloc>
				<Link to={Path.Profile(owner)}>
					<UserDetailedView user={owner} showDescription size={isMobile ? "L" : "XL"} />
				</Link>
				<Row gap={isMobile ? 20 : 53}>
					{members
						.filter((member) => member.id !== owner.id)
						.slice(0, 3)
						.map((member) => (
							<Link key={member.id} to={Path.Profile(member)}>
								<UserDetailedView user={member} showDescription size={isMobile ? "M" : "L"} />
							</Link>
						))}
				</Row>
			</Bloc>
		</Container>
	);
};

const Container = styled.div`
	width: 100%;

	${stack(10)}
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;

const Bloc = styled.div`
	${row(120, undefined, "center")};
	width: 100%;
	@media ${mobile} {
		${stack(20, "center", "center")};
		> :not(:last-child) {
			margin-right: 0px;
		}
	}
`;
